/**
 * Catalog of agreement types surfaced in the Drafting tab. This is metadata
 * only (id/label/description) for the bubble palette — the clarifying
 * questions for a given type are fetched from signus-service at selection
 * time (see `wordDraftQuestions.ts`), not hardcoded here.
 */

export type QuestionType = 'text' | 'longtext' | 'select';

export interface Question {
  id: string;
  prompt: string;
  type: QuestionType;
  /** Required for type === "select". Order is preserved in the UI. */
  options?: string[];
  placeholder?: string;
  /** Defaults to true. Set false for "anything else?" style questions. */
  required?: boolean;
}

export interface AgreementType {
  /** Stable id (kebab-case). Used as localStorage key for history. */
  id: string;
  /** Short label for the bubble (≤ ~22 chars to fit). */
  label: string;
  /** Full name used in the questions screen header and prompts. */
  fullName: string;
  /** Hover tooltip on the bubble. */
  description: string;
  /** True for ad-hoc custom requests (not in the predefined catalog). */
  isCustom?: boolean;
  /** Free-text description from the user (custom requests only). */
  customDescription?: string;
}

/** An AgreementType once its clarifying questions have been fetched from signus-service. */
export interface ResolvedAgreementType extends AgreementType {
  questions: Question[];
}

/** Generic catch-all appended after the fetched questions for every type. */
export const Q_ADDITIONAL: Question = {
  id: 'additional',
  prompt: 'Anything else to include?',
  type: 'longtext',
  required: false,
  placeholder: 'Optional — any extra terms, context, or constraints',
};

export const PREDEFINED_AGREEMENT_TYPES: AgreementType[] = [
  {
    id: 'nda',
    label: 'NDA',
    fullName: 'Non-Disclosure Agreement (NDA)',
    description: 'Mutual or one-way confidentiality agreement.',
  },
  {
    id: 'employment',
    label: 'Employment',
    fullName: 'Employment Agreement',
    description: 'Standard or executive employment contract.',
  },
  {
    id: 'contractor',
    label: 'Contractor',
    fullName: 'Independent Contractor Agreement',
    description: 'Engaging an independent contractor or consultant.',
  },
  {
    id: 'partnership',
    label: 'Partnership',
    fullName: 'Partnership Agreement',
    description: 'General or limited partnership agreement.',
  },
  {
    id: 'sales-purchase',
    label: 'Sales & Purchase',
    fullName: 'Sales and Purchase Agreement',
    description: 'Sale of goods, assets, or business interests.',
  },
  {
    id: 'service',
    label: 'Service Agreement',
    fullName: 'Service Contract / Agreement (MSA-style)',
    description: 'Master services agreement for ongoing work.',
  },
  {
    id: 'lease',
    label: 'Lease/Rental',
    fullName: 'Lease / Rental Agreement',
    description: 'Residential or commercial lease.',
  },
  {
    id: 'ip-assignment',
    label: 'IP Assignment',
    fullName: 'Intellectual Property (IP) Assignment Agreement',
    description: 'Transfer of IP rights from assignor to assignee.',
  },
  {
    id: 'non-compete',
    label: 'Non-Compete',
    fullName: 'Non-Compete Agreement',
    description: 'Restrictive covenant on competition.',
  },
  {
    id: 'mou-loi',
    label: 'MoU / LOI',
    fullName: 'Memorandum of Understanding (MoU) / Letter of Intent',
    description: 'Preliminary understanding, binding or non-binding.',
  },
];

export function buildCustomAgreementType(description: string): AgreementType {
  const trimmed = description.trim();
  const labelText = trimmed.length > 22 ? trimmed.slice(0, 22).trim() + '…' : trimmed;
  return {
    id: `custom-${Date.now()}`,
    label: labelText || 'Custom',
    fullName: trimmed || 'Custom agreement',
    description: trimmed,
    isCustom: true,
    customDescription: trimmed,
  };
}

export function getPredefinedById(id: string): AgreementType | undefined {
  return PREDEFINED_AGREEMENT_TYPES.find(t => t.id === id);
}
